// src/core/directives/dialogAutoFocus.ts
import { Directive, nextTick } from 'vue';

const focusableSelector = [
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const dialogAutoFocus: Directive<HTMLElement, boolean> = {
  mounted(el, binding) {
    if (binding.value) focusFirst(el);
  },

  updated(el, binding) {
    if (!binding.oldValue && binding.value) {
      focusFirst(el);
    }
  },
};

function focusFirst(el: HTMLElement) {
  nextTick(() => {
    window.setTimeout(() => {
      const target =
        findAutofocusTarget(el) ||
        el.querySelector<HTMLElement>(focusableSelector) ||
        findDialogBodyOrFooterFocusable(el);

      if (!target) return;

      target.focus({ preventScroll: true });
    }, 80);
  });
}

function findAutofocusTarget(el: HTMLElement) {
  const markedElement = el.querySelector<HTMLElement>('[data-autofocus]');

  if (!markedElement) return null;

  if (markedElement.matches(focusableSelector)) {
    return markedElement;
  }

  return markedElement.querySelector<HTMLElement>(focusableSelector);
}

function findDialogBodyOrFooterFocusable(el: HTMLElement) {
  const dialog =
    el.closest('.el-dialog') ||
    el.closest('.modal');

  if (!dialog) return null;

  const containers = dialog.querySelectorAll<HTMLElement>(
    '.el-dialog__body, .el-dialog__footer, .modal-body, .modal-footer'
  );

  for (const container of Array.from(containers)) {
    const target = container.querySelector<HTMLElement>(focusableSelector);

    if (target) return target;
  }

  return null;
}

export function blurActiveElement(): void {
  const active = document.activeElement;

  if (active instanceof HTMLElement && active !== document.body) {
    active.blur();
  }
}

export default dialogAutoFocus;
